// components/widgets/Stakeholders/FinalBeliefScreen.jsx
import React from "react";
import { ChevronDown } from "lucide-react";
import PandaWave from "./PandaWave";
import userAvatar from "../../../../../assets/Panda_headshot.png";

import UnmuteIcon from "../../../../../assets/meeting/Unmute.png";
import StartVideoIcon from "../../../../../assets/meeting/Start Video.png";
import SecurityShieldIcon from "../../../../../assets/meeting/Security.png";
import ParticipantsIcon from "../../../../../assets/meeting/Participants.png";
import ChatIcon from "../../../../../assets/meeting/Chat.png";
import ShareScreenIcon from "../../../../../assets/meeting/Share Screen.png";
import PollingIcon from "../../../../../assets/meeting/Polling.png";
import RecordIcon from "../../../../../assets/meeting/Record.png";
import BreakoutIcon from "../../../../../assets/meeting/Breakout.png";
import ReactionIcon from "../../../../../assets/meeting/Reaction.png";

const FinalBeliefScreen = ({
  user = { name: "You", avatar: userAvatar },
  onNextStep,
}) => {
  const resolvedUser = user || { name: "You", avatar: userAvatar };
  return (
    <div className="w-full h-full bg-[#EAEFF4] flex items-center justify-center">
      <div className="w-full max-w-6xl bg-black rounded-xl border border-[#3BA7FF] overflow-hidden flex flex-col">

        {/* --------------------------- */}
        {/* TOP BAR */}
        {/* --------------------------- */}
        <div className="w-full bg-[#2B2B2B] px-4 py-2 flex items-center relative">

          {/* Mac bubbles */}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
            <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
            <div className="w-3 h-3 rounded-full bg-[#28C840]" />
          </div>

          {/* CENTER */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-2 bg-[#1E1E1E] border border-[#3A3A3A] px-3 py-[6px] rounded-md text-[#DADADA] text-sm cursor-pointer">
              Original Sound: Off
              <ChevronDown size={16} className="text-[#DADADA]" />
            </div>
          </div>

          {/* RIGHT */}
          <button className="ml-auto bg-[#1E1E1E] border border-[#3A3A3A] px-3 py-[6px] rounded-md text-[#DADADA] text-sm">
            View
          </button>
        </div>

        {/* --------------------------- */}
        {/* MAIN CONTENT */}
        {/* --------------------------- */}
        <div className="flex w-full h-full bg-black p-10">

          {/* LEFT TILE — PANDA */}
          <div className="flex-1 bg-[#111] rounded-xl border border-[#54B0AF] p-6 mr-6 relative flex items-center justify-center">
            <PandaWave />
            <div className="absolute bottom-3 left-3 bg-black/70 px-2 py-1 text-xs rounded">
              Sto – Career Companion
            </div>
          </div>

          {/* RIGHT TILE — USER */}
          <div className="flex-1 bg-[#111] rounded-xl border border-gray-600 p-6 relative flex items-center justify-center">
            <div className="relative w-36 h-36 rounded-full border-[3px] border-gray-300 overflow-hidden">
              <img
                src={resolvedUser.avatar || userAvatar}
                alt={resolvedUser.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="absolute bottom-3 left-3 bg-black/70 px-2 py-1 text-xs rounded flex items-center gap-1">
              <span className="text-red-500">🎤</span> {user.name}
            </div>
          </div>
        </div>

        {/* --------------------------- */}
        {/* CENTER MESSAGE */}
        {/* --------------------------- */}
        <div className="text-center px-10 pb-12">
          <h2 className="text-2xl font-semibold text-white">
            Good job {resolvedUser.name}, on identifying these behavioural patterns.
          </h2>
          <p className="text-gray-400 mt-2">
            Now, let’s dive into the underlying cause for these.
          </p>
<button
  onClick={onNextStep}
  className="mt-8 px-8 py-3 bg-[#E64545] text-white rounded-full flex items-center gap-2 mx-auto"
>
  End Meeting
  <span className="text-lg">➜</span>
</button>

        </div>

        {/* --------------------------- */}
        {/* ZOOM-LIKE TOOLBAR */}
        {/* --------------------------- */}
        <div className="w-full bg-[#1C1C1C] py-3 px-10 flex items-center justify-between">

          {/* LEFT ICON GROUP */}
          <div className="flex items-center gap-10">

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={UnmuteIcon} alt="unmute-icon" className="w-6 h-6 mb-1" />
              <span>Mute</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={StartVideoIcon} alt="start-video-icon" className="w-6 h-6 mb-1" />
              <span>Start Video</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={SecurityShieldIcon} alt="security-shield-icon" className="w-6 h-6 mb-1" />
              <span>Security</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={ParticipantsIcon} alt="participants-icon" className="w-6 h-6 mb-1" />
              <span>Participants</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={ChatIcon} alt="chat-icon" className="w-6 h-6 mb-1" />
              <span>Chat</span>
            </div>

            <div className="flex flex-col items-center text-[#6CFF7F] text-[11px] cursor-pointer">
              <img src={ShareScreenIcon} alt="share-screen-icon" className="w-6 h-6 mb-1" />
              <span className="text-white">Share Screen</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={PollingIcon} alt="polling-icon" className="w-6 h-6 mb-1" />
              <span>Polling</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={RecordIcon} alt="record-icon" className="w-6 h-6 mb-1" />
              <span>Record</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={BreakoutIcon} alt="breakout-icon" className="w-6 h-6 mb-1" />
              <span>Breakout Rooms</span>
            </div>

            <div className="flex flex-col items-center text-gray-200 text-[11px] cursor-pointer">
              <img src={ReactionIcon} alt="reaction-icon" className="w-6 h-6 mb-1" />
              <span>Reactions</span>
            </div>
          </div>

          {/* END BUTTON */}
          <button className="bg-[#E64545] px-5 py-2 rounded-md text-white text-sm font-medium">
            End
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinalBeliefScreen;
